/* eslint-disable */
import { Coin } from "cosmjs-types/cosmos/base/v1beta1/coin";
import Long from "long";
import _m0 from "protobufjs/minimal";
import { BlockDescriptors } from "./block_descriptor";
import { GenesisInfo } from "./genesis_info";
import { RollappMetadata } from "./metadata";
import { Rollapp_VMType, rollapp_VMTypeFromJSON, rollapp_VMTypeToJSON } from "./rollapp";

export const protobufPackage = "dymensionxyz.dymension.rollapp";

/** MsgCreateRollapp creates a new rollapp chain on the hub. */
export interface MsgCreateRollapp {
  /** creator is the bech32-encoded address of the rollapp creator */
  creator: string;
  /**
   * rollappId is the unique identifier of the rollapp chain.
   * The rollapp_id follows the same standard as cosmos chain_id
   */
  rollappId: string;
  /**
   * initial_sequencer takes one or more coma-separated bech32-encoded addresses of the
   * sequencer(s) that are allowed to initially serve this rollappId.
   */
  initialSequencer: string;
  /** see Rollapp for more details */
  minSequencerBond:
    | Coin
    | undefined;
  /** alias is the chain alias used for display and namespace system */
  alias: string;
  /** metadata is the rollapp metadata */
  metadata:
    | RollappMetadata
    | undefined;
  /** genesis_info is the genesis information */
  genesisInfo:
    | GenesisInfo
    | undefined;
  /** vm_type is the type of rollapp machine: EVM or WASM */
  vmType: Rollapp_VMType;
}

export interface MsgCreateRollappResponse {
}

/** MsgUpdateRollappInformation updates the rollapp information. */
export interface MsgUpdateRollappInformation {
  /** owner is the bech32-encoded address of the rollapp owner */
  owner: string;
  /** rollapp_id is the unique identifier of the rollapp chain. */
  rollappId: string;
  /**
   * initial_sequencer is one or more bech32-encoded address of the
   * sequencer that are allowed to initially serve this rollappId.
   * wildcard '*' means any sequencer is allowed to be the first proposer.
   */
  initialSequencer: string;
  /** see Rollapp for more details */
  minSequencerBond:
    | Coin
    | undefined;
  /** metadata is the rollapp metadata */
  metadata:
    | RollappMetadata
    | undefined;
  /** genesis_info is the genesis information */
  genesisInfo: GenesisInfo | undefined;
}

export interface MsgUpdateRollappInformationResponse {
}

/**
 * MsgUpdateState updates a rollapp state with a block batch.
 * a block batch is a list of ordered blocks (by height)
 */
export interface MsgUpdateState {
  /** creator is the bech32-encoded address of the sequencer sending the update */
  creator: string;
  /**
   * rollappId is the rollapp that the sequencer belongs to and asking to update
   * The rollappId follows the same standard as cosmos chain_id
   */
  rollappId: string;
  /** startHeight is the block height of the first block in the batch */
  startHeight: number;
  /** numBlocks is the number of blocks included in this batch update */
  numBlocks: number;
  /** DAPath is the description of the location on the DA layer */
  DAPath: string;
  /**
   * BDs is a list of block description objects (one per block)
   * the list must be ordered by height, starting from startHeight to startHeight+numBlocks-1
   */
  BDs:
    | BlockDescriptors
    | undefined;
  /** last is true if this is the last batch of the sequencer */
  last: boolean;
  /** rollapp_revision is the revision of the rollapp chain. increases after hard fork */
  rollappRevision: number;
}

export interface MsgUpdateStateResponse {
}

/** MsgTransferOwnership transfers the ownership of a rollapp chain to a new owner. */
export interface MsgTransferOwnership {
  /** current_owner is the bech32-encoded address of the current owner */
  currentOwner: string;
  /** new_owner is the bech32-encoded address of the new owner */
  newOwner: string;
  /** rollapp_id is the unique identifier of the rollapp chain. */
  rollappId: string;
}

export interface MsgTransferOwnershipResponse {
}

/** MsgAddApp adds an app to the rollapp. */
export interface MsgAddApp {
  /** creator is the bech32-encoded address of the app creator */
  creator: string;
  /** name is the unique App's name */
  name: string;
  /** rollapp_id is the id of the Rollapp the App belongs to */
  rollappId: string;
  /** description is the description of the App */
  description: string;
  /** image is the url to the App image */
  image: string;
  /** url is the URL to the App's website */
  url: string;
  /** order is the order of the App in the Rollapp */
  order: number;
}

export interface MsgAddAppResponse {
}

/** MsgUpdateApp updates the app information. */
export interface MsgUpdateApp {
  /** creator is the bech32-encoded address of the app owner */
  creator: string;
  /** id is the unique App's id in the Rollapp */
  id: number;
  /** name is the unique App's name (immutable) */
  name: string;
  /** rollapp_id is the id of the Rollapp the App belongs to */
  rollappId: string;
  /** description is the description of the App */
  description: string;
  /** image is the url to the App image */
  image: string;
  /** url is the URL to the App's website */
  url: string;
  /** order is the order of the App in the Rollapp */
  order: number;
}

export interface MsgUpdateAppResponse {
}

/** MsgRemoveApp removes an app from the rollapp. */
export interface MsgRemoveApp {
  /** creator is the bech32-encoded address of the app owner */
  creator: string;
  /** id is the unique App's id in the Rollapp */
  id: number;
  /** rollapp_id is the id of the Rollapp the App belongs to */
  rollappId: string;
}

export interface MsgRemoveAppResponse {
}

/**
 * MsgMarkObsoleteRollapps marks specified versions as obsolete as well as
 * all corresponding rollapps. Must be called by the governance.
 */
export interface MsgMarkObsoleteRollapps {
  /** Authority is the authority address. */
  authority: string;
  /** DrsVersions is a list of DRS versions that will be marked obsolete. */
  drsVersions: number[];
}

export interface MsgMarkObsoleteRollappsResponse {
}

function createBaseMsgCreateRollapp(): MsgCreateRollapp {
  return {
    creator: "",
    rollappId: "",
    initialSequencer: "",
    minSequencerBond: undefined,
    alias: "",
    metadata: undefined,
    genesisInfo: undefined,
    vmType: 0,
  };
}

export const MsgCreateRollapp = {
  encode(message: MsgCreateRollapp, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.creator !== "") {
      writer.uint32(10).string(message.creator);
    }
    if (message.rollappId !== "") {
      writer.uint32(18).string(message.rollappId);
    }
    if (message.initialSequencer !== "") {
      writer.uint32(90).string(message.initialSequencer);
    }
    if (message.minSequencerBond !== undefined) {
      Coin.encode(message.minSequencerBond, writer.uint32(130).fork()).ldelim();
    }
    if (message.alias !== "") {
      writer.uint32(98).string(message.alias);
    }
    if (message.metadata !== undefined) {
      RollappMetadata.encode(message.metadata, writer.uint32(106).fork()).ldelim();
    }
    if (message.genesisInfo !== undefined) {
      GenesisInfo.encode(message.genesisInfo, writer.uint32(114).fork()).ldelim();
    }
    if (message.vmType !== 0) {
      writer.uint32(120).int32(message.vmType);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgCreateRollapp {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgCreateRollapp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.creator = reader.string();
          break;
        case 2:
          message.rollappId = reader.string();
          break;
        case 11:
          message.initialSequencer = reader.string();
          break;
        case 16:
          message.minSequencerBond = Coin.decode(reader, reader.uint32());
          break;
        case 12:
          message.alias = reader.string();
          break;
        case 13:
          message.metadata = RollappMetadata.decode(reader, reader.uint32());
          break;
        case 14:
          message.genesisInfo = GenesisInfo.decode(reader, reader.uint32());
          break;
        case 15:
          message.vmType = reader.int32() as any;
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgCreateRollapp {
    return {
      creator: isSet(object.creator) ? String(object.creator) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      initialSequencer: isSet(object.initialSequencer) ? String(object.initialSequencer) : "",
      minSequencerBond: isSet(object.minSequencerBond) ? Coin.fromJSON(object.minSequencerBond) : undefined,
      alias: isSet(object.alias) ? String(object.alias) : "",
      metadata: isSet(object.metadata) ? RollappMetadata.fromJSON(object.metadata) : undefined,
      genesisInfo: isSet(object.genesisInfo) ? GenesisInfo.fromJSON(object.genesisInfo) : undefined,
      vmType: isSet(object.vmType) ? rollapp_VMTypeFromJSON(object.vmType) : 0,
    };
  },

  toJSON(message: MsgCreateRollapp): unknown {
    const obj: any = {};
    message.creator !== undefined && (obj.creator = message.creator);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.initialSequencer !== undefined && (obj.initialSequencer = message.initialSequencer);
    message.minSequencerBond !== undefined
      && (obj.minSequencerBond = message.minSequencerBond ? Coin.toJSON(message.minSequencerBond) : undefined);
    message.alias !== undefined && (obj.alias = message.alias);
    message.metadata !== undefined
      && (obj.metadata = message.metadata ? RollappMetadata.toJSON(message.metadata) : undefined);
    message.genesisInfo !== undefined
      && (obj.genesisInfo = message.genesisInfo ? GenesisInfo.toJSON(message.genesisInfo) : undefined);
    message.vmType !== undefined && (obj.vmType = rollapp_VMTypeToJSON(message.vmType));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgCreateRollapp>, I>>(object: I): MsgCreateRollapp {
    const message = createBaseMsgCreateRollapp();
    message.creator = object.creator ?? "";
    message.rollappId = object.rollappId ?? "";
    message.initialSequencer = object.initialSequencer ?? "";
    message.minSequencerBond = (object.minSequencerBond !== undefined && object.minSequencerBond !== null)
      ? Coin.fromPartial(object.minSequencerBond)
      : undefined;
    message.alias = object.alias ?? "";
    message.metadata = (object.metadata !== undefined && object.metadata !== null)
      ? RollappMetadata.fromPartial(object.metadata)
      : undefined;
    message.genesisInfo = (object.genesisInfo !== undefined && object.genesisInfo !== null)
      ? GenesisInfo.fromPartial(object.genesisInfo)
      : undefined;
    message.vmType = object.vmType ?? 0;
    return message;
  },
};

function createBaseMsgCreateRollappResponse(): MsgCreateRollappResponse {
  return {};
}

export const MsgCreateRollappResponse = {
  encode(_: MsgCreateRollappResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgCreateRollappResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgCreateRollappResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgCreateRollappResponse {
    return {};
  },

  toJSON(_: MsgCreateRollappResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgCreateRollappResponse>, I>>(_: I): MsgCreateRollappResponse {
    const message = createBaseMsgCreateRollappResponse();
    return message;
  },
};

function createBaseMsgUpdateRollappInformation(): MsgUpdateRollappInformation {
  return {
    owner: "",
    rollappId: "",
    initialSequencer: "",
    minSequencerBond: undefined,
    metadata: undefined,
    genesisInfo: undefined,
  };
}

export const MsgUpdateRollappInformation = {
  encode(message: MsgUpdateRollappInformation, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.owner !== "") {
      writer.uint32(10).string(message.owner);
    }
    if (message.rollappId !== "") {
      writer.uint32(18).string(message.rollappId);
    }
    if (message.initialSequencer !== "") {
      writer.uint32(26).string(message.initialSequencer);
    }
    if (message.minSequencerBond !== undefined) {
      Coin.encode(message.minSequencerBond, writer.uint32(58).fork()).ldelim();
    }
    if (message.metadata !== undefined) {
      RollappMetadata.encode(message.metadata, writer.uint32(42).fork()).ldelim();
    }
    if (message.genesisInfo !== undefined) {
      GenesisInfo.encode(message.genesisInfo, writer.uint32(50).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateRollappInformation {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateRollappInformation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.owner = reader.string();
          break;
        case 2:
          message.rollappId = reader.string();
          break;
        case 3:
          message.initialSequencer = reader.string();
          break;
        case 7:
          message.minSequencerBond = Coin.decode(reader, reader.uint32());
          break;
        case 5:
          message.metadata = RollappMetadata.decode(reader, reader.uint32());
          break;
        case 6:
          message.genesisInfo = GenesisInfo.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgUpdateRollappInformation {
    return {
      owner: isSet(object.owner) ? String(object.owner) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      initialSequencer: isSet(object.initialSequencer) ? String(object.initialSequencer) : "",
      minSequencerBond: isSet(object.minSequencerBond) ? Coin.fromJSON(object.minSequencerBond) : undefined,
      metadata: isSet(object.metadata) ? RollappMetadata.fromJSON(object.metadata) : undefined,
      genesisInfo: isSet(object.genesisInfo) ? GenesisInfo.fromJSON(object.genesisInfo) : undefined,
    };
  },

  toJSON(message: MsgUpdateRollappInformation): unknown {
    const obj: any = {};
    message.owner !== undefined && (obj.owner = message.owner);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.initialSequencer !== undefined && (obj.initialSequencer = message.initialSequencer);
    message.minSequencerBond !== undefined
      && (obj.minSequencerBond = message.minSequencerBond ? Coin.toJSON(message.minSequencerBond) : undefined);
    message.metadata !== undefined
      && (obj.metadata = message.metadata ? RollappMetadata.toJSON(message.metadata) : undefined);
    message.genesisInfo !== undefined
      && (obj.genesisInfo = message.genesisInfo ? GenesisInfo.toJSON(message.genesisInfo) : undefined);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateRollappInformation>, I>>(object: I): MsgUpdateRollappInformation {
    const message = createBaseMsgUpdateRollappInformation();
    message.owner = object.owner ?? "";
    message.rollappId = object.rollappId ?? "";
    message.initialSequencer = object.initialSequencer ?? "";
    message.minSequencerBond = (object.minSequencerBond !== undefined && object.minSequencerBond !== null)
      ? Coin.fromPartial(object.minSequencerBond)
      : undefined;
    message.metadata = (object.metadata !== undefined && object.metadata !== null)
      ? RollappMetadata.fromPartial(object.metadata)
      : undefined;
    message.genesisInfo = (object.genesisInfo !== undefined && object.genesisInfo !== null)
      ? GenesisInfo.fromPartial(object.genesisInfo)
      : undefined;
    return message;
  },
};

function createBaseMsgUpdateRollappInformationResponse(): MsgUpdateRollappInformationResponse {
  return {};
}

export const MsgUpdateRollappInformationResponse = {
  encode(_: MsgUpdateRollappInformationResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateRollappInformationResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateRollappInformationResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgUpdateRollappInformationResponse {
    return {};
  },

  toJSON(_: MsgUpdateRollappInformationResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateRollappInformationResponse>, I>>(
    _: I,
  ): MsgUpdateRollappInformationResponse {
    const message = createBaseMsgUpdateRollappInformationResponse();
    return message;
  },
};

function createBaseMsgUpdateState(): MsgUpdateState {
  return {
    creator: "",
    rollappId: "",
    startHeight: 0,
    numBlocks: 0,
    DAPath: "",
    BDs: undefined,
    last: false,
    rollappRevision: 0,
  };
}

export const MsgUpdateState = {
  encode(message: MsgUpdateState, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.creator !== "") {
      writer.uint32(10).string(message.creator);
    }
    if (message.rollappId !== "") {
      writer.uint32(18).string(message.rollappId);
    }
    if (message.startHeight !== 0) {
      writer.uint32(24).uint64(message.startHeight);
    }
    if (message.numBlocks !== 0) {
      writer.uint32(32).uint64(message.numBlocks);
    }
    if (message.DAPath !== "") {
      writer.uint32(42).string(message.DAPath);
    }
    if (message.BDs !== undefined) {
      BlockDescriptors.encode(message.BDs, writer.uint32(58).fork()).ldelim();
    }
    if (message.last === true) {
      writer.uint32(64).bool(message.last);
    }
    if (message.rollappRevision !== 0) {
      writer.uint32(72).uint64(message.rollappRevision);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateState {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateState();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.creator = reader.string();
          break;
        case 2:
          message.rollappId = reader.string();
          break;
        case 3:
          message.startHeight = longToNumber(reader.uint64() as Long);
          break;
        case 4:
          message.numBlocks = longToNumber(reader.uint64() as Long);
          break;
        case 5:
          message.DAPath = reader.string();
          break;
        case 7:
          message.BDs = BlockDescriptors.decode(reader, reader.uint32());
          break;
        case 8:
          message.last = reader.bool();
          break;
        case 9:
          message.rollappRevision = longToNumber(reader.uint64() as Long);
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgUpdateState {
    return {
      creator: isSet(object.creator) ? String(object.creator) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      startHeight: isSet(object.startHeight) ? Number(object.startHeight) : 0,
      numBlocks: isSet(object.numBlocks) ? Number(object.numBlocks) : 0,
      DAPath: isSet(object.DAPath) ? String(object.DAPath) : "",
      BDs: isSet(object.BDs) ? BlockDescriptors.fromJSON(object.BDs) : undefined,
      last: isSet(object.last) ? Boolean(object.last) : false,
      rollappRevision: isSet(object.rollappRevision) ? Number(object.rollappRevision) : 0,
    };
  },

  toJSON(message: MsgUpdateState): unknown {
    const obj: any = {};
    message.creator !== undefined && (obj.creator = message.creator);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.startHeight !== undefined && (obj.startHeight = Math.round(message.startHeight));
    message.numBlocks !== undefined && (obj.numBlocks = Math.round(message.numBlocks));
    message.DAPath !== undefined && (obj.DAPath = message.DAPath);
    message.BDs !== undefined && (obj.BDs = message.BDs ? BlockDescriptors.toJSON(message.BDs) : undefined);
    message.last !== undefined && (obj.last = message.last);
    message.rollappRevision !== undefined && (obj.rollappRevision = Math.round(message.rollappRevision));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateState>, I>>(object: I): MsgUpdateState {
    const message = createBaseMsgUpdateState();
    message.creator = object.creator ?? "";
    message.rollappId = object.rollappId ?? "";
    message.startHeight = object.startHeight ?? 0;
    message.numBlocks = object.numBlocks ?? 0;
    message.DAPath = object.DAPath ?? "";
    message.BDs = (object.BDs !== undefined && object.BDs !== null)
      ? BlockDescriptors.fromPartial(object.BDs)
      : undefined;
    message.last = object.last ?? false;
    message.rollappRevision = object.rollappRevision ?? 0;
    return message;
  },
};

function createBaseMsgUpdateStateResponse(): MsgUpdateStateResponse {
  return {};
}

export const MsgUpdateStateResponse = {
  encode(_: MsgUpdateStateResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateStateResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateStateResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgUpdateStateResponse {
    return {};
  },

  toJSON(_: MsgUpdateStateResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateStateResponse>, I>>(_: I): MsgUpdateStateResponse {
    const message = createBaseMsgUpdateStateResponse();
    return message;
  },
};

function createBaseMsgTransferOwnership(): MsgTransferOwnership {
  return { currentOwner: "", newOwner: "", rollappId: "" };
}

export const MsgTransferOwnership = {
  encode(message: MsgTransferOwnership, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.currentOwner !== "") {
      writer.uint32(10).string(message.currentOwner);
    }
    if (message.newOwner !== "") {
      writer.uint32(18).string(message.newOwner);
    }
    if (message.rollappId !== "") {
      writer.uint32(26).string(message.rollappId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgTransferOwnership {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgTransferOwnership();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.currentOwner = reader.string();
          break;
        case 2:
          message.newOwner = reader.string();
          break;
        case 3:
          message.rollappId = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgTransferOwnership {
    return {
      currentOwner: isSet(object.currentOwner) ? String(object.currentOwner) : "",
      newOwner: isSet(object.newOwner) ? String(object.newOwner) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
    };
  },

  toJSON(message: MsgTransferOwnership): unknown {
    const obj: any = {};
    message.currentOwner !== undefined && (obj.currentOwner = message.currentOwner);
    message.newOwner !== undefined && (obj.newOwner = message.newOwner);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgTransferOwnership>, I>>(object: I): MsgTransferOwnership {
    const message = createBaseMsgTransferOwnership();
    message.currentOwner = object.currentOwner ?? "";
    message.newOwner = object.newOwner ?? "";
    message.rollappId = object.rollappId ?? "";
    return message;
  },
};

function createBaseMsgTransferOwnershipResponse(): MsgTransferOwnershipResponse {
  return {};
}

export const MsgTransferOwnershipResponse = {
  encode(_: MsgTransferOwnershipResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgTransferOwnershipResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgTransferOwnershipResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgTransferOwnershipResponse {
    return {};
  },

  toJSON(_: MsgTransferOwnershipResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgTransferOwnershipResponse>, I>>(_: I): MsgTransferOwnershipResponse {
    const message = createBaseMsgTransferOwnershipResponse();
    return message;
  },
};

function createBaseMsgAddApp(): MsgAddApp {
  return { creator: "", name: "", rollappId: "", description: "", image: "", url: "", order: 0 };
}

export const MsgAddApp = {
  encode(message: MsgAddApp, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.creator !== "") {
      writer.uint32(10).string(message.creator);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.rollappId !== "") {
      writer.uint32(26).string(message.rollappId);
    }
    if (message.description !== "") {
      writer.uint32(34).string(message.description);
    }
    if (message.image !== "") {
      writer.uint32(42).string(message.image);
    }
    if (message.url !== "") {
      writer.uint32(50).string(message.url);
    }
    if (message.order !== 0) {
      writer.uint32(56).int32(message.order);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgAddApp {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgAddApp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.creator = reader.string();
          break;
        case 2:
          message.name = reader.string();
          break;
        case 3:
          message.rollappId = reader.string();
          break;
        case 4:
          message.description = reader.string();
          break;
        case 5:
          message.image = reader.string();
          break;
        case 6:
          message.url = reader.string();
          break;
        case 7:
          message.order = reader.int32();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgAddApp {
    return {
      creator: isSet(object.creator) ? String(object.creator) : "",
      name: isSet(object.name) ? String(object.name) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      description: isSet(object.description) ? String(object.description) : "",
      image: isSet(object.image) ? String(object.image) : "",
      url: isSet(object.url) ? String(object.url) : "",
      order: isSet(object.order) ? Number(object.order) : 0,
    };
  },

  toJSON(message: MsgAddApp): unknown {
    const obj: any = {};
    message.creator !== undefined && (obj.creator = message.creator);
    message.name !== undefined && (obj.name = message.name);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.description !== undefined && (obj.description = message.description);
    message.image !== undefined && (obj.image = message.image);
    message.url !== undefined && (obj.url = message.url);
    message.order !== undefined && (obj.order = Math.round(message.order));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgAddApp>, I>>(object: I): MsgAddApp {
    const message = createBaseMsgAddApp();
    message.creator = object.creator ?? "";
    message.name = object.name ?? "";
    message.rollappId = object.rollappId ?? "";
    message.description = object.description ?? "";
    message.image = object.image ?? "";
    message.url = object.url ?? "";
    message.order = object.order ?? 0;
    return message;
  },
};

function createBaseMsgAddAppResponse(): MsgAddAppResponse {
  return {};
}

export const MsgAddAppResponse = {
  encode(_: MsgAddAppResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgAddAppResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgAddAppResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgAddAppResponse {
    return {};
  },

  toJSON(_: MsgAddAppResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgAddAppResponse>, I>>(_: I): MsgAddAppResponse {
    const message = createBaseMsgAddAppResponse();
    return message;
  },
};

function createBaseMsgUpdateApp(): MsgUpdateApp {
  return { creator: "", id: 0, name: "", rollappId: "", description: "", image: "", url: "", order: 0 };
}

export const MsgUpdateApp = {
  encode(message: MsgUpdateApp, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.creator !== "") {
      writer.uint32(10).string(message.creator);
    }
    if (message.id !== 0) {
      writer.uint32(16).uint64(message.id);
    }
    if (message.name !== "") {
      writer.uint32(26).string(message.name);
    }
    if (message.rollappId !== "") {
      writer.uint32(34).string(message.rollappId);
    }
    if (message.description !== "") {
      writer.uint32(42).string(message.description);
    }
    if (message.image !== "") {
      writer.uint32(50).string(message.image);
    }
    if (message.url !== "") {
      writer.uint32(58).string(message.url);
    }
    if (message.order !== 0) {
      writer.uint32(64).int32(message.order);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateApp {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateApp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.creator = reader.string();
          break;
        case 2:
          message.id = longToNumber(reader.uint64() as Long);
          break;
        case 3:
          message.name = reader.string();
          break;
        case 4:
          message.rollappId = reader.string();
          break;
        case 5:
          message.description = reader.string();
          break;
        case 6:
          message.image = reader.string();
          break;
        case 7:
          message.url = reader.string();
          break;
        case 8:
          message.order = reader.int32();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgUpdateApp {
    return {
      creator: isSet(object.creator) ? String(object.creator) : "",
      id: isSet(object.id) ? Number(object.id) : 0,
      name: isSet(object.name) ? String(object.name) : "",
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      description: isSet(object.description) ? String(object.description) : "",
      image: isSet(object.image) ? String(object.image) : "",
      url: isSet(object.url) ? String(object.url) : "",
      order: isSet(object.order) ? Number(object.order) : 0,
    };
  },

  toJSON(message: MsgUpdateApp): unknown {
    const obj: any = {};
    message.creator !== undefined && (obj.creator = message.creator);
    message.id !== undefined && (obj.id = Math.round(message.id));
    message.name !== undefined && (obj.name = message.name);
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.description !== undefined && (obj.description = message.description);
    message.image !== undefined && (obj.image = message.image);
    message.url !== undefined && (obj.url = message.url);
    message.order !== undefined && (obj.order = Math.round(message.order));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateApp>, I>>(object: I): MsgUpdateApp {
    const message = createBaseMsgUpdateApp();
    message.creator = object.creator ?? "";
    message.id = object.id ?? 0;
    message.name = object.name ?? "";
    message.rollappId = object.rollappId ?? "";
    message.description = object.description ?? "";
    message.image = object.image ?? "";
    message.url = object.url ?? "";
    message.order = object.order ?? 0;
    return message;
  },
};

function createBaseMsgUpdateAppResponse(): MsgUpdateAppResponse {
  return {};
}

export const MsgUpdateAppResponse = {
  encode(_: MsgUpdateAppResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgUpdateAppResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgUpdateAppResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgUpdateAppResponse {
    return {};
  },

  toJSON(_: MsgUpdateAppResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgUpdateAppResponse>, I>>(_: I): MsgUpdateAppResponse {
    const message = createBaseMsgUpdateAppResponse();
    return message;
  },
};

function createBaseMsgRemoveApp(): MsgRemoveApp {
  return { creator: "", id: 0, rollappId: "" };
}

export const MsgRemoveApp = {
  encode(message: MsgRemoveApp, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.creator !== "") {
      writer.uint32(10).string(message.creator);
    }
    if (message.id !== 0) {
      writer.uint32(16).uint64(message.id);
    }
    if (message.rollappId !== "") {
      writer.uint32(26).string(message.rollappId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgRemoveApp {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgRemoveApp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.creator = reader.string();
          break;
        case 2:
          message.id = longToNumber(reader.uint64() as Long);
          break;
        case 3:
          message.rollappId = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgRemoveApp {
    return {
      creator: isSet(object.creator) ? String(object.creator) : "",
      id: isSet(object.id) ? Number(object.id) : 0,
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
    };
  },

  toJSON(message: MsgRemoveApp): unknown {
    const obj: any = {};
    message.creator !== undefined && (obj.creator = message.creator);
    message.id !== undefined && (obj.id = Math.round(message.id));
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgRemoveApp>, I>>(object: I): MsgRemoveApp {
    const message = createBaseMsgRemoveApp();
    message.creator = object.creator ?? "";
    message.id = object.id ?? 0;
    message.rollappId = object.rollappId ?? "";
    return message;
  },
};

function createBaseMsgRemoveAppResponse(): MsgRemoveAppResponse {
  return {};
}

export const MsgRemoveAppResponse = {
  encode(_: MsgRemoveAppResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgRemoveAppResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgRemoveAppResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgRemoveAppResponse {
    return {};
  },

  toJSON(_: MsgRemoveAppResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgRemoveAppResponse>, I>>(_: I): MsgRemoveAppResponse {
    const message = createBaseMsgRemoveAppResponse();
    return message;
  },
};

function createBaseMsgMarkObsoleteRollapps(): MsgMarkObsoleteRollapps {
  return { authority: "", drsVersions: [] };
}

export const MsgMarkObsoleteRollapps = {
  encode(message: MsgMarkObsoleteRollapps, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.authority !== "") {
      writer.uint32(10).string(message.authority);
    }
    writer.uint32(18).fork();
    for (const v of message.drsVersions) {
      writer.uint32(v);
    }
    writer.ldelim();
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgMarkObsoleteRollapps {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgMarkObsoleteRollapps();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.authority = reader.string();
          break;
        case 2:
          if ((tag & 7) === 2) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.drsVersions.push(reader.uint32());
            }
          } else {
            message.drsVersions.push(reader.uint32());
          }
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MsgMarkObsoleteRollapps {
    return {
      authority: isSet(object.authority) ? String(object.authority) : "",
      drsVersions: Array.isArray(object?.drsVersions) ? object.drsVersions.map((e: any) => Number(e)) : [],
    };
  },

  toJSON(message: MsgMarkObsoleteRollapps): unknown {
    const obj: any = {};
    message.authority !== undefined && (obj.authority = message.authority);
    if (message.drsVersions) {
      obj.drsVersions = message.drsVersions.map((e) => Math.round(e));
    } else {
      obj.drsVersions = [];
    }
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgMarkObsoleteRollapps>, I>>(object: I): MsgMarkObsoleteRollapps {
    const message = createBaseMsgMarkObsoleteRollapps();
    message.authority = object.authority ?? "";
    message.drsVersions = object.drsVersions?.map((e) => e) || [];
    return message;
  },
};

function createBaseMsgMarkObsoleteRollappsResponse(): MsgMarkObsoleteRollappsResponse {
  return {};
}

export const MsgMarkObsoleteRollappsResponse = {
  encode(_: MsgMarkObsoleteRollappsResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MsgMarkObsoleteRollappsResponse {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMsgMarkObsoleteRollappsResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(_: any): MsgMarkObsoleteRollappsResponse {
    return {};
  },

  toJSON(_: MsgMarkObsoleteRollappsResponse): unknown {
    const obj: any = {};
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<MsgMarkObsoleteRollappsResponse>, I>>(_: I): MsgMarkObsoleteRollappsResponse {
    const message = createBaseMsgMarkObsoleteRollappsResponse();
    return message;
  },
};

/** Msg defines the Msg service. */
export interface Msg {
  CreateRollapp(request: MsgCreateRollapp): Promise<MsgCreateRollappResponse>;
  UpdateRollappInformation(request: MsgUpdateRollappInformation): Promise<MsgUpdateRollappInformationResponse>;
  UpdateState(request: MsgUpdateState): Promise<MsgUpdateStateResponse>;
  TransferOwnership(request: MsgTransferOwnership): Promise<MsgTransferOwnershipResponse>;
  AddApp(request: MsgAddApp): Promise<MsgAddAppResponse>;
  UpdateApp(request: MsgUpdateApp): Promise<MsgUpdateAppResponse>;
  RemoveApp(request: MsgRemoveApp): Promise<MsgRemoveAppResponse>;
  MarkObsoleteRollapps(request: MsgMarkObsoleteRollapps): Promise<MsgMarkObsoleteRollappsResponse>;
}

export class MsgClientImpl implements Msg {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.CreateRollapp = this.CreateRollapp.bind(this);
    this.UpdateRollappInformation = this.UpdateRollappInformation.bind(this);
    this.UpdateState = this.UpdateState.bind(this);
    this.TransferOwnership = this.TransferOwnership.bind(this);
    this.AddApp = this.AddApp.bind(this);
    this.UpdateApp = this.UpdateApp.bind(this);
    this.RemoveApp = this.RemoveApp.bind(this);
    this.MarkObsoleteRollapps = this.MarkObsoleteRollapps.bind(this);
  }
  CreateRollapp(request: MsgCreateRollapp): Promise<MsgCreateRollappResponse> {
    const data = MsgCreateRollapp.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "CreateRollapp", data);
    return promise.then((data) => MsgCreateRollappResponse.decode(new _m0.Reader(data)));
  }

  UpdateRollappInformation(request: MsgUpdateRollappInformation): Promise<MsgUpdateRollappInformationResponse> {
    const data = MsgUpdateRollappInformation.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "UpdateRollappInformation", data);
    return promise.then((data) => MsgUpdateRollappInformationResponse.decode(new _m0.Reader(data)));
  }

  UpdateState(request: MsgUpdateState): Promise<MsgUpdateStateResponse> {
    const data = MsgUpdateState.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "UpdateState", data);
    return promise.then((data) => MsgUpdateStateResponse.decode(new _m0.Reader(data)));
  }

  TransferOwnership(request: MsgTransferOwnership): Promise<MsgTransferOwnershipResponse> {
    const data = MsgTransferOwnership.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "TransferOwnership", data);
    return promise.then((data) => MsgTransferOwnershipResponse.decode(new _m0.Reader(data)));
  }

  AddApp(request: MsgAddApp): Promise<MsgAddAppResponse> {
    const data = MsgAddApp.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "AddApp", data);
    return promise.then((data) => MsgAddAppResponse.decode(new _m0.Reader(data)));
  }

  UpdateApp(request: MsgUpdateApp): Promise<MsgUpdateAppResponse> {
    const data = MsgUpdateApp.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "UpdateApp", data);
    return promise.then((data) => MsgUpdateAppResponse.decode(new _m0.Reader(data)));
  }

  RemoveApp(request: MsgRemoveApp): Promise<MsgRemoveAppResponse> {
    const data = MsgRemoveApp.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "RemoveApp", data);
    return promise.then((data) => MsgRemoveAppResponse.decode(new _m0.Reader(data)));
  }

  MarkObsoleteRollapps(request: MsgMarkObsoleteRollapps): Promise<MsgMarkObsoleteRollappsResponse> {
    const data = MsgMarkObsoleteRollapps.encode(request).finish();
    const promise = this.rpc.request("dymensionxyz.dymension.rollapp.Msg", "MarkObsoleteRollapps", data);
    return promise.then((data) => MsgMarkObsoleteRollappsResponse.decode(new _m0.Reader(data)));
  }
}

interface Rpc {
  request(service: string, method: string, data: Uint8Array): Promise<Uint8Array>;
}

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
